import { motion, useReducedMotion } from 'framer-motion'
import type { ReactNode } from 'react'
import { cn } from '@/lib/utils'

const EASE = [0.16, 1, 0.3, 1] as const

interface PageHeroProps {
  eyebrow: string
  /** Rendered in the metallic sheen. */
  title: string
  /** Optional second line in brand blue. */
  accent?: string
  lede?: ReactNode
  /** CTAs, stats or meta line under the lede. */
  children?: ReactNode
  /** Right-hand visual on large screens (product preview, diagram). */
  aside?: ReactNode
  className?: string
}

/**
 * Opening band for interior public pages — same light, grid and type as the
 * home hero, at a calmer scale. Pages without an `aside` get a single column.
 */
export function PageHero({ eyebrow, title, accent, lede, children, aside, className }: PageHeroProps) {
  const reduce = useReducedMotion()
  const rise = (delay: number) => ({
    initial: reduce ? false : { opacity: 0, y: 14 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.8, delay, ease: EASE },
  })

  return (
    <section className={cn('grain relative w-full overflow-hidden px-6 pt-32 pb-16 md:pt-40 md:pb-20', className)}>
      <div
        aria-hidden
        className="pointer-events-none absolute -top-[30rem] left-1/2 h-[46rem] w-[72rem] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,color-mix(in_srgb,var(--color-primary)_18%,transparent),transparent)]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 [background-image:linear-gradient(var(--color-border)_1px,transparent_1px),linear-gradient(90deg,var(--color-border)_1px,transparent_1px)] [background-size:72px_72px] [mask-image:radial-gradient(ellipse_70%_60%_at_50%_0%,black,transparent)]"
      />

      <div
        className={cn(
          'relative z-10 mx-auto grid max-w-6xl grid-cols-1 items-center gap-14',
          aside && 'lg:grid-cols-12 lg:gap-12',
        )}
      >
        <div className={cn(aside ? 'lg:col-span-6' : 'max-w-3xl')}>
          <motion.p {...rise(0)} className="eyebrow flex items-center gap-3">
            <span aria-hidden className="h-px w-8 bg-primary" />
            {eyebrow}
          </motion.p>
          <motion.h1
            {...rise(0.08)}
            className="mt-6 pb-1 text-[clamp(2.5rem,5.5vw,4.75rem)] font-semibold leading-[0.95] tracking-[-0.05em] text-balance"
          >
            <span className="text-sheen">{title}</span>
            {accent && (
              <>
                <br />
                <span className="text-brand">{accent}</span>
              </>
            )}
          </motion.h1>
          {lede && (
            <motion.div
              {...rise(0.18)}
              className="mt-6 max-w-2xl text-lg leading-relaxed text-pretty text-[var(--color-text-secondary)]"
            >
              {lede}
            </motion.div>
          )}
          {children && (
            <motion.div {...rise(0.28)} className="mt-9">
              {children}
            </motion.div>
          )}
        </div>
        {aside && (
          <motion.div {...rise(0.35)} className="lg:col-span-6">
            {aside}
          </motion.div>
        )}
      </div>
    </section>
  )
}
